import { escapeHTML } from '@lumapps/utils/string/escapeHTML';

import { isElement } from '../../../slate/utils/isElement';
import { isText } from '../../../slate/utils/isText';
import type { Wrex } from '../../../types';
import type { HTMLSerializeOptions } from './types';

function convertTextNode(node: Wrex.Text, options: HTMLSerializeOptions): string {
    const { text, ...marks } = node;

    if (text === '') {
        return '';
    }

    return Object.keys(marks).reduce((acc, mark) => {
        const markValue = (marks as Record<string, unknown>)[mark];
        if (!markValue) {
            return acc;
        }
        const markConverter = options.marks[mark];
        if (markConverter) {
            return markConverter(acc, markValue === true ? undefined : markValue);
        }
        return acc;
    }, escapeHTML(text));
}

/**
 * Convert a slate node into an HTML string.
 */
function convertNode(node: Wrex.Node, options: HTMLSerializeOptions): string {
    if (isText(node)) {
        return convertTextNode(node, options);
    }

    const children = (node.children || []).map((child) => convertNode(child, options)).join('');

    if (isElement(node)) {
        const elementConverter = options.elements[node.type];
        if (elementConverter) {
            return elementConverter(node, children);
        }
    }

    // Unknown slate node: returning children.
    return children;
}

/**
 * Convert slate nodes into an HTML string.
 *
 * @param nodes   Slate nodes to serialize.
 * @param options Serialization options (mark and element converters).
 * @return An HTML string.
 */
export function toHTML(nodes: Wrex.Nodes, options: HTMLSerializeOptions): string {
    return nodes.map((node) => convertNode(node, options)).join('');
}
